import { useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../../lib/trpc.js";
import { useT } from "../../i18n/index.js";
import { fmt } from "./finance-utils.js";
// ParseResult/ParsedRow — espelham o retorno de finance.import.preview (csv-parser
// do backend). Definidos localmente porque o tipo do StormFin standalone
// (@stormfin/api) não existe no V3.
interface ParsedRow {
  tempId: string;
  date: string;
  description: string;
  amountIn: number;
  amountOut: number;
  externalId?: string; // ID único do banco (ex.: Wise) — chave forte de dedup; repassado ao confirm
}
interface ParseResult {
  rows: ParsedRow[];
  bank: string;
  separator: string;
  headerLine: number;
  accountName: string;
  columns: string[];
  errors: string[];
}

type Company = "BR Storms" | "Euro Storm" | "Geral";

export function ImportPage() {
  const t = useT();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [preview, setPreview] = useState<ParseResult | null>(null);
  const [company, setCompany] = useState<Company>("Euro Storm");
  const [feedback, setFeedback] = useState("");

  const previewMut = trpc.finance.import.preview.useMutation();
  const confirmMut = trpc.finance.import.confirm.useMutation({
    onSuccess: (res) => {
      qc.invalidateQueries();
      setPreview(null);
      setFeedback(
        `✓ ${res.imported} ${t("financeImport.entriesImported")}${res.skipped ? ` · ${res.skipped} ${t("financeImport.duplicatesSkipped")}` : ""}`,
      );
      setTimeout(() => setFeedback(""), 5000);
    },
  });

  async function handleFile(file: File) {
    const text = await readFileWithEncoding(file);
    previewMut.mutate(
      { content: text, filename: file.name },
      {
        onSuccess: (result) => {
          setPreview(result);
          // Auto-detecta empresa pelo nome da conta bancária
          if (result.accountName) {
            const n = result.accountName.toLowerCase();
            if (n.includes("euro")) setCompany("Euro Storm");
            else if (n.includes("br ")) setCompany("BR Storms");
          }
        },
      },
    );
  }

  return (
    <div>
      {feedback && (
        <div
          className="alert"
          style={{
            marginBottom: 16,
            borderColor: "rgba(62,207,142,.3)",
            background: "rgba(62,207,142,.08)",
            color: "var(--green)",
          }}
        >
          {feedback}
        </div>
      )}

      {!preview ? (
        <>
          {/* Drop zone */}
          <div
            style={{
              border: `1.5px dashed ${dragging ? "var(--accent)" : "var(--border2)"}`,
              borderRadius: "var(--r2)",
              padding: 40,
              textAlign: "center",
              cursor: "pointer",
              transition: "all .2s",
              background: dragging ? "rgba(79,142,247,.05)" : "var(--bg2)",
            }}
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              const f = e.dataTransfer.files[0];
              if (f) handleFile(f);
            }}
          >
            <div style={{ fontSize: 32, marginBottom: 12 }}>📂</div>
            <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 6 }}>
              {t("financeImport.dropzoneTitle")}
            </div>
            <div style={{ fontSize: 13, color: "var(--text2)" }}>
              {t("financeImport.dropzoneHint")}
            </div>
            <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 8 }}>
              {t("financeImport.dropzoneFormats")}
              <br />
              {t("financeImport.dropzoneEncoding")}
            </div>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.txt"
            style={{ display: "none" }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
              e.target.value = "";
            }}
          />

          <div style={{ marginTop: 24, display: "flex", gap: 10, alignItems: "center" }}>
            <span style={{ fontSize: 13, color: "var(--text3)" }}>
              {t("financeImport.orAddManually")}
            </span>
            <button
              className="btn primary"
              onClick={() => {
                /* TODO: modal */
              }}
            >
              {t("financeImport.newEntryBtn")}
            </button>
          </div>

          {previewMut.isPending && (
            <div style={{ marginTop: 24, color: "var(--text3)", fontSize: 13 }}>
              {t("financeImport.parsingFile")}
            </div>
          )}
          {previewMut.isError && (
            <div
              className="alert"
              style={{
                marginTop: 16,
                borderColor: "rgba(255,100,100,.3)",
                background: "rgba(255,100,100,.08)",
                color: "var(--red)",
              }}
            >
              ⚠️ {previewMut.error.message}
            </div>
          )}
        </>
      ) : (
        <ImportPreview
          result={preview}
          company={company}
          onCompanyChange={setCompany}
          onConfirm={() => confirmMut.mutate({ rows: preview.rows, company })}
          onCancel={() => setPreview(null)}
          loading={confirmMut.isPending}
        />
      )}
    </div>
  );
}

function ImportPreview({
  result,
  company,
  onCompanyChange,
  onConfirm,
  onCancel,
  loading,
}: {
  result: ParseResult;
  company: Company;
  onCompanyChange: (c: Company) => void;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
}) {
  const t = useT();
  return (
    <div>
      <div className="alert" style={{ marginBottom: 16 }}>
        <strong>
          {result.rows.length} {t("financeImport.transactionsDetected")}
        </strong>
        {" · "} {t("financeImport.bankLabel")}: {result.bank}
        {" · "} {t("financeImport.separatorLabel")}:{" "}
        <code style={{ fontFamily: "var(--mono)", fontSize: 11 }}>{result.separator}</code>
        {" · "} {t("financeImport.headerLineLabel")} {result.headerLine}
        {result.accountName && (
          <>
            {" "}
            · {t("financeImport.accountLabel")}: <strong>{result.accountName}</strong>
          </>
        )}
      </div>

      <div className="section-title">{t("financeImport.previewTitle")}</div>
      <div className="table-wrap" style={{ marginBottom: 16 }}>
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>{t("financeImport.colDate")}</th>
              <th>{t("financeImport.colDescription")}</th>
              <th>{t("financeImport.colAmount")}</th>
              <th>{t("financeImport.colStatus")}</th>
            </tr>
          </thead>
          <tbody>
            {result.rows.slice(0, 30).map((r, i) => {
              const v =
                r.amountIn > 0
                  ? `+${fmt(r.amountIn)}`
                  : r.amountOut > 0
                    ? `-${fmt(r.amountOut)}`
                    : "—";
              const cls = r.amountIn > 0 ? "val-pos" : r.amountOut > 0 ? "val-neg" : "val-zero";
              return (
                <tr key={r.tempId}>
                  <td style={{ color: "var(--text3)", fontSize: 11 }}>{i + 1}</td>
                  <td className="mono" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                    {r.date || "—"}
                  </td>
                  <td
                    style={{
                      maxWidth: 320,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={r.description}
                  >
                    {r.description}
                  </td>
                  <td className={cls}>{v}</td>
                  <td>
                    <span className="dot pending" />
                    {t("financeImport.statusPending")}
                  </td>
                </tr>
              );
            })}
            {result.rows.length > 30 && (
              <tr>
                <td
                  colSpan={5}
                  style={{ textAlign: "center", color: "var(--text3)", fontSize: 12, padding: 10 }}
                >
                  {t("financeImport.andMorePre")} {result.rows.length - 30}{" "}
                  {t("financeImport.andMoreSuf")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <label style={{ fontSize: 13, color: "var(--text2)", whiteSpace: "nowrap" }}>
            {t("financeImport.statementCompanyLabel")}
          </label>
          <select
            className="input"
            style={{ width: "auto" }}
            value={company}
            onChange={(e) => onCompanyChange(e.target.value as Company)}
          >
            <option value="Euro Storm">Euro Storm</option>
            <option value="BR Storms">BR Storms</option>
            <option value="Geral">{t("financeImport.companyGeneral")}</option>
          </select>
        </div>
        <button className="btn primary" onClick={onConfirm} disabled={loading}>
          {loading ? t("financeImport.importing") : t("financeImport.confirmImportBtn")}
        </button>
        <button className="btn danger" onClick={onCancel}>
          {t("financeImport.cancel")}
        </button>
      </div>
    </div>
  );
}

// Detecta encoding: tenta UTF-8, fallback para ISO-8859-1
async function readFileWithEncoding(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = (e) => {
      const text = e.target!.result as string;
      if (text.includes("\uFFFD")) {
        const r2 = new FileReader();
        r2.onload = (e2) => resolve(e2.target!.result as string);
        r2.onerror = reject;
        r2.readAsText(file, "ISO-8859-1");
      } else {
        resolve(text);
      }
    };
    r.onerror = reject;
    r.readAsText(file, "UTF-8");
  });
}
